/**
 * Local player ship with GLB model and thruster effects.
 * Uses velocity-based sub-tick extrapolation for smooth rendering.
 * Position: visualPos = physicsPos + velocity * alpha * TICK_DELTA
 * Rotation: exponential lerp (Rapier doesn't simulate angular velocity for ships).
 */

import { useRef, Suspense } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { TICK_DELTA } from '@warmancer/shared';
import { Ship, PlaceholderShip } from './models/Ship';
import { ThrusterExhaust } from './effects/ThrusterExhaust';
import { MuzzleFlash } from './effects/MuzzleFlash';
import { useGameStore } from '../stores/gameStore';

const MODEL_PATH = '/assets/models/ships/Fighter1.glb';
const SHIP_SIZE = 2;

// Rotation lerp speed: how fast the visual rotation catches up to the physics rotation.
// Kept as exponential lerp because Rapier doesn't simulate angular velocity for ships
// (rotation is set directly each tick). Position stutter is far more visible than
// rotation stutter, and this speed is aggressive enough for smooth turns.
const ROTATION_LERP_SPEED = 50;

/**
 * Shortest-path angle interpolation handling the -PI/+PI wrap.
 */
function lerpAngle(from: number, to: number, t: number): number {
  let diff = to - from;
  // Normalize to [-PI, PI]
  while (diff > Math.PI) diff -= Math.PI * 2;
  while (diff < -Math.PI) diff += Math.PI * 2;
  return from + diff * t;
}

export function PlayerShip() {
  const groupRef = useRef<THREE.Group>(null);
  const thrusting = useGameStore((s) => s.localShip?.thrusting ?? false);
  const afterburner = useGameStore((s) => s.localShip?.afterburner ?? false);
  const braking = useGameStore((s) => s.localShip?.braking ?? false);
  const strafingLeft = useGameStore((s) => s.localShip?.strafingLeft ?? false);
  const strafingRight = useGameStore((s) => s.localShip?.strafingRight ?? false);
  const firing = useGameStore((s) => s.localShip?.firing ?? false);

  // Render rotation smoothly follows the physics rotation
  const renderRotation = useRef({ value: 0, initialized: false });

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    const ship = useGameStore.getState().localShip;
    if (!ship) return;
    const alpha = useGameStore.getState().renderAlpha;

    // Velocity-based sub-tick extrapolation for position.
    // Produces constant visual velocity between physics ticks regardless of frame
    // rate or timing variance. At alpha=0, visual equals physics position exactly.
    const targetX = ship.x + ship.vx * alpha * TICK_DELTA;
    const targetZ = ship.y + ship.vy * alpha * TICK_DELTA;

    if (!renderRotation.current.initialized) {
      renderRotation.current.value = ship.rotation;
      renderRotation.current.initialized = true;
    } else {
      // Exponential lerp for rotation only (no angular velocity from Rapier)
      const t = Math.min(1, ROTATION_LERP_SPEED * delta);
      renderRotation.current.value = lerpAngle(renderRotation.current.value, ship.rotation, t);
    }

    // In top-down view: x -> x, y -> z (Three.js), rotation around Y axis
    groupRef.current.position.x = targetX;
    groupRef.current.position.z = targetZ;
    groupRef.current.rotation.y = -renderRotation.current.value - Math.PI / 2;
  });

  return (
    <group ref={groupRef}>
      <Suspense fallback={<PlaceholderShip targetSize={SHIP_SIZE} />}>
        <Ship modelPath={MODEL_PATH} targetSize={SHIP_SIZE} />
      </Suspense>
      {/* Main engines (rear) */}
      <group position={[0, 0, 0.8]}>
        <ThrusterExhaust active={thrusting && !braking} afterburner={afterburner} />
      </group>
      {/* Brake thruster (center, beneath hull, reverse) */}
      <group position={[0, -0.15, 0]}>
        <ThrusterExhaust active={braking} afterburner={afterburner} reverse />
      </group>
      {/* Right-side thruster (fires when strafing LEFT — exhaust goes right, pushes ship left) */}
      <group position={[0, -0.15, 0]} rotation={[0, Math.PI / 2, 0]}>
        <ThrusterExhaust active={strafingLeft} afterburner={afterburner} />
      </group>
      {/* Left-side thruster (fires when strafing RIGHT — exhaust goes left, pushes ship right) */}
      <group position={[0, -0.15, 0]} rotation={[0, -Math.PI / 2, 0]}>
        <ThrusterExhaust active={strafingRight} afterburner={afterburner} />
      </group>
      {/* Weapon muzzle flash at ship nose */}
      <group position={[0, 0.1, -1.0]}>
        <MuzzleFlash active={firing} />
      </group>
    </group>
  );
}
